import { Component } from "@/types";
import { TEXT_SHADOW_PRESETS, TYPOGRAPHY_SCALE } from "../constants/text-command-constants";

export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

export const CSS_COLOR_RGB_MAP: Record<string, RgbColor> = {
  red: { r: 255, g: 0, b: 0 },
  blue: { r: 0, g: 0, b: 255 },
  green: { r: 0, g: 128, b: 0 },
  yellow: { r: 255, g: 255, b: 0 },
  orange: { r: 255, g: 165, b: 0 },
  purple: { r: 128, g: 0, b: 128 },
  pink: { r: 255, g: 192, b: 203 },
  black: { r: 0, g: 0, b: 0 },
  white: { r: 255, g: 255, b: 255 },
  gray: { r: 128, g: 128, b: 128 },
  grey: { r: 128, g: 128, b: 128 },
  brown: { r: 165, g: 42, b: 42 },
  cyan: { r: 0, g: 255, b: 255 },
  magenta: { r: 255, g: 0, b: 255 },
  navy: { r: 0, g: 0, b: 128 },
  indigo: { r: 75, g: 0, b: 130 },
  teal: { r: 0, g: 128, b: 128 },
  coral: { r: 255, g: 127, b: 80 },
  salmon: { r: 250, g: 128, b: 114 },
  crimson: { r: 220, g: 20, b: 60 },
  maroon: { r: 128, g: 0, b: 0 },
  olive: { r: 128, g: 128, b: 0 },
  lime: { r: 0, g: 255, b: 0 },
  aqua: { r: 0, g: 255, b: 255 },
  turquoise: { r: 64, g: 224, b: 208 },
  violet: { r: 238, g: 130, b: 238 },
  plum: { r: 221, g: 160, b: 221 },
  gold: { r: 255, g: 215, b: 0 },
  silver: { r: 192, g: 192, b: 192 },
  beige: { r: 245, g: 245, b: 220 },
  ivory: { r: 255, g: 255, b: 240 },
  lavender: { r: 230, g: 230, b: 250 },
  mint: { r: 152, g: 255, b: 152 },
  peach: { r: 255, g: 203, b: 164 },
  tan: { r: 210, g: 180, b: 140 },
  sienna: { r: 160, g: 82, b: 45 },
  azure: { r: 240, g: 255, b: 255 },
  chocolate: { r: 210, g: 105, b: 30 },
  tomato: { r: 255, g: 99, b: 71 },
  khaki: { r: 240, g: 230, b: 140 },
  orchid: { r: 218, g: 112, b: 214 },
  peru: { r: 205, g: 133, b: 63 },
  thistle: { r: 216, g: 191, b: 216 },
  wheat: { r: 245, g: 222, b: 179 },
  linen: { r: 250, g: 240, b: 230 },
  snow: { r: 255, g: 250, b: 250 },
  honeydew: { r: 240, g: 255, b: 240 },
  seashell: { r: 255, g: 245, b: 238 },
  firebrick: { r: 178, g: 34, b: 34 },
  darkred: { r: 139, g: 0, b: 0 },
  darkorange: { r: 255, g: 140, b: 0 },
  darkgreen: { r: 0, g: 100, b: 0 },
  darkblue: { r: 0, g: 0, b: 139 },
  lightblue: { r: 173, g: 216, b: 230 },
  lightgreen: { r: 144, g: 238, b: 144 },
  lightgray: { r: 211, g: 211, b: 211 },
  lightgrey: { r: 211, g: 211, b: 211 },
  lightpink: { r: 255, g: 182, b: 193 },
  skyblue: { r: 135, g: 206, b: 235 },
  steelblue: { r: 70, g: 130, b: 180 },
  slategray: { r: 112, g: 128, b: 144 },
  slategrey: { r: 112, g: 128, b: 144 },
  royalblue: { r: 65, g: 105, b: 225 },
  midnightblue: { r: 25, g: 25, b: 112 },
  forestgreen: { r: 34, g: 139, b: 34 },
  limegreen: { r: 50, g: 205, b: 50 },
  springgreen: { r: 0, g: 255, b: 127 },
  seagreen: { r: 46, g: 139, b: 87 },
  mediumseagreen: { r: 60, g: 179, b: 113 },
  darkseagreen: { r: 143, g: 188, b: 143 },
  palegreen: { r: 152, g: 251, b: 152 },
  mediumaquamarine: { r: 102, g: 205, b: 170 },
  cadetblue: { r: 95, g: 158, b: 160 },
  powderblue: { r: 176, g: 224, b: 230 },
  cornflowerblue: { r: 100, g: 149, b: 237 },
  deepskyblue: { r: 0, g: 191, b: 255 },
  dodgerblue: { r: 30, g: 144, b: 255 },
  mediumblue: { r: 0, g: 0, b: 205 },
  blueviolet: { r: 138, g: 43, b: 226 },
  darkorchid: { r: 153, g: 50, b: 204 },
  darkviolet: { r: 148, g: 0, b: 211 },
  mediumpurple: { r: 147, g: 112, b: 219 },
  mediumorchid: { r: 186, g: 85, b: 211 },
  mediumvioletred: { r: 199, g: 21, b: 133 },
  palevioletred: { r: 219, g: 112, b: 147 },
  deeppink: { r: 255, g: 20, b: 147 },
  hotpink: { r: 255, g: 105, b: 180 },
  lightcoral: { r: 240, g: 128, b: 128 },
  indianred: { r: 205, g: 92, b: 92 },
  rosybrown: { r: 188, g: 143, b: 143 },
  sandybrown: { r: 244, g: 164, b: 96 },
  goldenrod: { r: 218, g: 165, b: 32 },
  darkgoldenrod: { r: 184, g: 134, b: 11 },
  burlywood: { r: 222, g: 184, b: 135 },
  bisque: { r: 255, g: 228, b: 196 },
  navajowhite: { r: 255, g: 222, b: 173 },
  papayawhip: { r: 255, g: 239, b: 213 },
  moccasin: { r: 255, g: 228, b: 181 },
  oldlace: { r: 253, g: 245, b: 230 },
  floralwhite: { r: 255, g: 250, b: 240 },
  ghostwhite: { r: 248, g: 248, b: 255 },
  aliceblue: { r: 240, g: 248, b: 255 },
  antiquewhite: { r: 250, g: 235, b: 215 },
  mistyrose: { r: 255, g: 228, b: 225 },
  lavenderblush: { r: 255, g: 240, b: 245 },
  lemonchiffon: { r: 255, g: 250, b: 205 },
  cornsilk: { r: 255, g: 248, b: 220 },
};

/**
 * Parse a color string to RGB components
 * Supports hex (#RGB, #RRGGBB), rgb(), rgba(), and CSS color names
 */
export function parseToRgb(color: string): RgbColor | null {
  const c = color.trim().toLowerCase();

  // Hex color
  if (c.startsWith('#')) {
    let hex = c.slice(1);
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }

  // RGB/RGBA
  const rgbMatch = c.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1]),
      g: parseInt(rgbMatch[2]),
      b: parseInt(rgbMatch[3]),
    };
  }

  if (CSS_COLOR_RGB_MAP[c]) {
    return CSS_COLOR_RGB_MAP[c];
  }

  return null;
}

/**
 * Adjust color brightness
 * @param color - The color string to adjust
 * @param amount - Positive for lighter, negative for darker (e.g., 0.2 = 20% lighter)
 * @returns Adjusted color as hex string
 */
export function adjustColorBrightness(color: string, amount: number): string | null {
  const rgb = parseToRgb(color);
  if (!rgb) return null;

  const adjust = (value: number) => {
    if (amount > 0) {
      // Lighter: move towards 255
      return Math.round(value + (255 - value) * amount);
    } else {
      // Darker: move towards 0
      return Math.round(value * (1 + amount));
    }
  };

  const newR = Math.max(0, Math.min(255, adjust(rgb.r)));
  const newG = Math.max(0, Math.min(255, adjust(rgb.g)));
  const newB = Math.max(0, Math.min(255, adjust(rgb.b)));

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

/**
 * Extract color from an existing textShadow CSS value
 * Returns the rgba/rgb/hex color if found, or null
 */
export function extractShadowColor(textShadow: string | undefined): string | null {
  if (!textShadow || textShadow === 'none') return null;

  // Match rgba(...) or rgb(...)
  const rgbaMatch = textShadow.match(/rgba?\([^)]+\)/i);
  if (rgbaMatch) return rgbaMatch[0];

  // Match hex color
  const hexMatch = textShadow.match(/#[0-9a-fA-F]{3,8}/);
  if (hexMatch) return hexMatch[0];

  // Match named color at the end
  const namedColorMatch = textShadow.match(/\s([a-zA-Z]+)$/);
  if (namedColorMatch) return namedColorMatch[1];

  return null;
}

/**
 * Build a textShadow value with specified size preset and color
 */
export function buildTextShadow(sizePreset: string, color?: string | null): string {
  const preset = TEXT_SHADOW_PRESETS[sizePreset] || TEXT_SHADOW_PRESETS['md'];
  if (!color) return preset;

  // Extract offset and blur from preset (e.g., "4px 4px 8px rgba(0, 0, 0, 0.6)")
  const parts = preset.match(/^(\d+px)\s+(\d+px)\s+(\d+px)/);
  if (parts) {
    return `${parts[1]} ${parts[2]} ${parts[3]} ${color}`;
  }
  return preset;
}

/**
 * Extract the offset and blur from an existing textShadow CSS value
 * Returns { offsetX, offsetY, blur } if found, or null
 */
export function extractShadowSize(textShadow: string | undefined): { offsetX: string; offsetY: string; blur: string } | null {
  if (!textShadow || textShadow === 'none') return null;

  // Match offset and blur values (e.g., "4px 4px 8px" from "4px 4px 8px rgba(...)")
  const match = textShadow.match(/^(\d+px)\s+(\d+px)\s+(\d+px)/);
  if (match) {
    return { offsetX: match[1], offsetY: match[2], blur: match[3] };
  }
  return null;
}

/**
 * Build a textShadow value with specified color, preserving existing size
 */
export function buildTextShadowWithColor(existingTextShadow: string | undefined, newColor: string): string {
  const existingSize = extractShadowSize(existingTextShadow);
  if (existingSize) {
    return `${existingSize.offsetX} ${existingSize.offsetY} ${existingSize.blur} ${newColor}`;
  }
  // Default to md size if no existing shadow
  return `2px 2px 4px ${newColor}`;
}

/**
 * Clamp font size to valid range
 */
export function clampFontSize(size: number): number {
  return Math.round(Math.max(8, Math.min(72, size)));
}

/**
 * Get next size in typography scale
 */
export function getNextTypographySize(currentSize: number): number {
  const closestIndex = TYPOGRAPHY_SCALE.findIndex((s, i) =>
    s >= currentSize || i === TYPOGRAPHY_SCALE.length - 1
  );
  const nextIndex = Math.min(closestIndex + 1, TYPOGRAPHY_SCALE.length - 1);
  return TYPOGRAPHY_SCALE[nextIndex];
}

/**
 * Get previous size in typography scale
 */
export function getPreviousTypographySize(currentSize: number): number {
  const closestIndex = TYPOGRAPHY_SCALE.findIndex((s, i) =>
    s >= currentSize || i === TYPOGRAPHY_SCALE.length - 1
  );
  const prevIndex = Math.max(closestIndex - 1, 0);
  return TYPOGRAPHY_SCALE[prevIndex];
}

/**
 * Parse font size from component (handles various formats)
 */
export function parseComponentFontSize(component: Component): number {
  const fromProps = component.properties?.fontSize;
  const fromStyle = component.style?.fontSize;

  if (typeof fromProps === 'number') return fromProps;
  if (typeof fromStyle === 'number') return fromStyle;
  if (typeof fromProps === 'string') {
    const parsed = parseFloat(fromProps);
    if (!isNaN(parsed)) return parsed;
  }
  if (typeof fromStyle === 'string') {
    const parsed = parseFloat(fromStyle);
    if (!isNaN(parsed)) return parsed;
  }
  return 16; // Default font size
}
